import { ArrowDown, ArrowUp } from 'lucide-react';
import React from 'react';
import { cn } from '../lib/utils';
import { Card, CardContent } from './Card';

/**
 * Metronic-styled Metric Card Component
 * Professional stat display with trend indicators
 *
 * @param {string} title - Card title/label
 * @param {number} value - Numeric value to display
 * @param {string} color - Color theme (orange, blue, navy, purple, green, red)
 * @param {boolean} loading - Loading state
 * @param {number} trend - Optional trend percentage (positive or negative)
 * @param {string} icon - Optional icon component
 */
const MetricCard = ({
  title,
  value = 0,
  color = 'blue',
  loading = false,
  trend,
  icon: Icon
}) => {
  const formattedValue = typeof value === 'number' ? value.toLocaleString() : '0';

  // Color configurations matching Metronic's design system
  const colorConfig = {
    orange: {
      border: 'border-l-4 border-orange-500',
      icon: 'text-orange-500',
      bg: 'bg-orange-50 dark:bg-orange-900/10'
    },
    blue: {
      border: 'border-l-4 border-blue-500',
      icon: 'text-blue-500',
      bg: 'bg-blue-50 dark:bg-blue-900/10'
    },
    navy: {
      border: 'border-l-4 border-indigo-600',
      icon: 'text-indigo-600',
      bg: 'bg-indigo-50 dark:bg-indigo-900/10'
    },
    purple: {
      border: 'border-l-4 border-purple-500',
      icon: 'text-purple-500',
      bg: 'bg-purple-50 dark:bg-purple-900/10'
    },
    green: {
      border: 'border-l-4 border-green-500',
      icon: 'text-green-500',
      bg: 'bg-green-50 dark:bg-green-900/10'
    },
    red: {
      border: 'border-l-4 border-red-500',
      icon: 'text-red-500',
      bg: 'bg-red-50 dark:bg-red-900/10'
    }
  };

  const colors = colorConfig[color] || colorConfig.blue;

  return (
    <Card className={cn("h-full shadow-sm hover:shadow-md transition-shadow", colors.border)}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Title */}
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              {title}
            </h3>

            {/* Value */}
            {loading ? (
              <div className="h-10 bg-muted rounded animate-pulse w-3/4"></div>
            ) : (
              <div className="flex items-end gap-2">
                <p className="text-3xl font-bold text-foreground">
                  {formattedValue}
                </p>

                {/* Trend Indicator */}
                {trend !== undefined && trend !== null && (
                  <div className={cn(
                    "flex items-center gap-1 text-sm font-medium mb-1",
                    trend >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {trend >= 0 ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )}
                    <span>{Math.abs(trend)}%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Icon */}
          {Icon && (
            <div className={cn(
              "p-3 rounded-lg",
              colors.bg
            )}>
              <Icon className={cn("h-6 w-6", colors.icon)} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MetricCard;

